import type {
  BackgroundAdjustments,
  DiffuseAdjustments,
  LoadedMockupAssets,
  ReflectionAdjustments,
  ScreenOverlayAdjustments
} from './composite';
import { renderMaster } from './composite';
import {
  buildMotionColorMatteOverlay,
  isBackgroundTintActive,
  resolveActiveMatteChannels,
  warmMotionColorMatteCompositor,
  type MotionColorMatteAdjustments
} from './colorMatte';
import { createCanvas, loadVideoFromUrl, seekVideo } from './loadImage';
import type {
  CustomPlacement,
  MockupTemplate,
  MotionAtlasContract,
  MotionAtlasLayout,
  MotionResizeMode,
  Point,
  ScreenWarpMode
} from '../types';
import {
  getMotionPlaybackDuration,
  getTemplateInputCount,
  normalizeFlexibleDisplay,
  getMotionTemplatePlaybackTime,
  isMultiScreenTrackingDocument,
  resolveMotionAtlasAuxRole
} from '@motionpitch/template-contract';

type MotionAtlasVariant = 'preview' | 'export';

export interface MotionAtlasSpec {
  columns: number;
  rows: number;
  tileWidth: number;
  tileHeight: number;
  atlasWidth: number;
  atlasHeight: number;
}

export interface MotionTrackingFrame {
  index: number;
  time: number;
  screenQuad: Point[];
}

export interface MotionTrackingSequence {
  fps: number;
  frameCount: number;
  duration: number;
  outputWidth: number;
  outputHeight: number;
  frames: MotionTrackingFrame[];
  frameMap: Map<number, MotionTrackingFrame>;
  bounds: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  };
  staticQuad: boolean;
}

export interface LoadedMotionAssets {
  template: MockupTemplate;
  variant: MotionAtlasVariant;
  atlasVideo: HTMLVideoElement;
  tracking: MotionTrackingSequence;
  tracking2: MotionTrackingSequence | null;
  tracking3: MotionTrackingSequence | null;
  atlasSpec: MotionAtlasSpec;
  /**
   * Puzzle Matte source, present only for templates that declare the capability
   * and ship the file. Null for every template published before the feature, so
   * nothing downstream can enter the colour path by accident.
   */
  colorMatteVideo: HTMLVideoElement | null;
  colorMatteUrl: string | null;
  colorMatteLoading: Promise<HTMLVideoElement> | null;
  /**
   * Black-and-white background separator, loaded alongside the colour matte.
   * Null for a template that ships none, which is what hides the Background row.
   */
  backgroundMatteVideo: HTMLVideoElement | null;
  backgroundMatteUrl: string | null;
  backgroundMatteLoading: Promise<HTMLVideoElement> | null;
  /**
   * Atlas whose recolourable regions are grey, loaded on demand the first time
   * the viewer switches Custom colors on. Null until then, and null forever for
   * a template that never shipped one — recolouring then falls back to the
   * ordinary atlas, which tints less accurately but still works.
   */
  neutralAtlasVideo: HTMLVideoElement | null;
  neutralAtlasUrl: string | null;
  neutralAtlasLoading: Promise<HTMLVideoElement> | null;
  /** True only after the neutral preview atlas has been aligned to the live frame. */
  neutralAtlasReady: boolean;
}

export interface MotionRenderOptions {
  renderSize?: number;
  screenRenderScale?: number;
  gridX?: number;
  gridY?: number;
  diffuse?: DiffuseAdjustments;
  background?: BackgroundAdjustments;
  reflection?: ReflectionAdjustments;
  screenOverlay?: ScreenOverlayAdjustments;
  useAlternateMask?: boolean;
  customPlacement?: CustomPlacement | null;
  customPlacement2?: CustomPlacement | null;
  customPlacement3?: CustomPlacement | null;
  warpMode?: ScreenWarpMode;
  warpMode2?: ScreenWarpMode;
  warpMode3?: ScreenWarpMode;
  userSource2?: CanvasImageSource | null;
  userSource3?: CanvasImageSource | null;
  realtime?: boolean;
  atlasSourceOverride?: CanvasImageSource;
  /**
   * Decoded Puzzle Matte frame. Export supplies one from its own decoder
   * pipeline; preview leaves it out and the seeked matte video is used instead.
   */
  colorMatteSourceOverride?: CanvasImageSource;
  /** Decoded background separator frame, supplied by the export decoder. */
  backgroundMatteSourceOverride?: CanvasImageSource;
  /** Per-channel user colours. Absent or all-disabled means no recolouring. */
  colorMatte?: MotionColorMatteAdjustments;
  trackingQuadOverride?: Point[];
  trackingQuadOverride2?: Point[];
  trackingQuadOverride3?: Point[];
}

export interface MotionResizeDimensions {
  outputWidth: number;
  outputHeight: number;
  sourceCropX: number;
  sourceCropY: number;
  sourceCropWidth: number;
  sourceCropHeight: number;
}

const DEFAULT_OUTPUT_SIZE = 1920;
const DEFAULT_FPS = 30;
const MOTION_WIDESCREEN_ASPECT = 16 / 9;
const MOTION_VERTICAL_ASPECT = 9 / 16;
const TRACKING_FRAME_OFFSET = 0;
// Export uses interpolateTrackingQuadAtTime (motionExport.ts -> trackingQuadOverride).
// Preview offset of +1 overshoots in export, so the export path uses 0 (no shift).
const TRACKING_FRAME_EXPORT_OFFSET = 0;

export async function loadMotionAssets(
  template: MockupTemplate,
  variant: MotionAtlasVariant
): Promise<LoadedMotionAssets> {
  const atlasUrl = variant === 'preview' ? template.previewAtlasUrl : template.atlasUrl;
  const trackingUrl = template.trackingUrl;

  if (!atlasUrl) {
    throw new Error(`Motion ${variant} atlas is missing for this template.`);
  }
  if (!trackingUrl) {
    throw new Error('Motion tracking JSON is missing for this template.');
  }

  // The matte is optional and must never be able to break a template: a missing
  // or unreadable file degrades to "no colour controls", not to a failed load.
  const colorMatteUrl =
    template.motionColorMatte && template.colorMatteUrl ? template.colorMatteUrl : null;
  const backgroundMatteUrl = template.backgroundMatteUrl || null;

  const [atlasVideo, trackingResult] = await Promise.all([
    loadVideoFromUrl(atlasUrl),
    loadMotionTracking(trackingUrl, template)
  ]);
  const atlasSpec = getMotionAtlasSpec(template.atlasLayout || 'standard-4', variant);

  if (atlasVideo.videoWidth !== atlasSpec.atlasWidth || atlasVideo.videoHeight !== atlasSpec.atlasHeight) {
    throw new Error(
      `Motion atlas has invalid size: expected ${atlasSpec.atlasWidth}x${atlasSpec.atlasHeight}, got ${atlasVideo.videoWidth}x${atlasVideo.videoHeight}.`
    );
  }

  atlasVideo.pause();
  atlasVideo.muted = true;
  atlasVideo.playsInline = true;

  const neutralAtlasUrl =
    (variant === 'preview' ? template.neutralPreviewAtlasUrl : template.neutralAtlasUrl) || null;

  const assets: LoadedMotionAssets = {
    template,
    variant,
    atlasVideo,
    tracking: trackingResult.primary,
    tracking2: trackingResult.secondary,
    tracking3: trackingResult.tertiary,
    atlasSpec,
    colorMatteVideo: null,
    colorMatteUrl,
    colorMatteLoading: null,
    backgroundMatteVideo: null,
    backgroundMatteUrl,
    backgroundMatteLoading: null,
    neutralAtlasVideo: null,
    neutralAtlasUrl: template.motionColorMatte ? neutralAtlasUrl : null,
    neutralAtlasLoading: null,
    neutralAtlasReady: false
  };

  if (colorMatteUrl || backgroundMatteUrl) {
    warmMotionColorMatteCompositor();
  }

  // The matte is fetched alongside, never in front of, the first frame. Waiting
  // on it would hold the preview behind another video download for a capability
  // that starts switched off anyway, so opening a template with a matte would
  // feel slower than opening one without. It drops into place when it arrives.
  // Preview loading remains best-effort, but the original promises are retained
  // so export can await the exact same request and reject instead of silently
  // producing an uncoloured frame.
  if (colorMatteUrl) {
    void loadColorMatteVideo(assets).catch(() => undefined);
  }
  // Background replacement is less common than object recolouring and its
  // source is a full-resolution video. Loading it here reserves another video
  // decoder even while the switch is off, which can push Chromium-family
  // browsers onto a slower software path. It is loaded on demand instead.

  return assets;
}

export function getMotionAtlasSpec(layout: MotionAtlasLayout, variant: MotionAtlasVariant): MotionAtlasSpec {
  const tileSize = variant === 'preview' ? 640 : 1920;
  if (layout === 'extended-6') {
    return {
      columns: 3,
      rows: 2,
      tileWidth: tileSize,
      tileHeight: tileSize,
      atlasWidth: tileSize * 3,
      atlasHeight: tileSize * 2
    };
  }

  return {
    columns: 2,
    rows: 2,
    tileWidth: tileSize,
    tileHeight: tileSize,
    atlasWidth: tileSize * 2,
    atlasHeight: tileSize * 2
  };
}

export function resolveMotionAtlasContract(
  template: Pick<MockupTemplate, 'atlasContract' | 'atlasLayout' | 'screenProfile' | 'flexibleDisplay'>
): MotionAtlasContract {
  const duo = normalizeFlexibleDisplay(template.flexibleDisplay);
  if (duo && duo.mode !== 'outer') return 'duo-packed-v1';
  if (
    template.atlasContract === 'screen-overlay-v1' ||
    template.atlasContract === 'legacy-reflection-v1' ||
    template.atlasContract === 'dual-screen-packed-v1' ||
    template.atlasContract === 'triple-screen-packed-v1' || template.atlasContract === 'duo-packed-v1'
  ) {
    return template.atlasContract;
  }

  if (template.screenProfile?.id === 'dual-screen') {
    return 'dual-screen-packed-v1';
  }
  if (template.screenProfile?.id === 'triple-screen') {
    return 'triple-screen-packed-v1';
  }
  return template.atlasLayout === 'extended-6' ? 'legacy-reflection-v1' : 'screen-overlay-v1';
}

export function getMotionFrameIndex(sequence: MotionTrackingSequence, currentTime: number): number {
  const rawIndex = Math.floor(Math.max(0, currentTime) * sequence.fps);
  return Math.max(0, Math.min(sequence.frameCount - 1, rawIndex));
}

export function getMotionFrameTime(sequence: MotionTrackingSequence, frameIndex: number): number {
  return Math.max(0, Math.min(frameIndex, sequence.frameCount - 1)) / Math.max(1, sequence.fps);
}

export function getMotionAtlasFrameTime(sequence: MotionTrackingSequence, frameIndex: number): number {
  const fps = Math.max(1, sequence.fps);
  const clampedIndex = Math.max(0, Math.min(frameIndex, sequence.frameCount - 1));
  const frameTime = clampedIndex / fps;
  const insideFrameOffset = 0.25 / fps;
  return clampMotionSequenceTime(sequence, frameTime + insideFrameOffset);
}

export function getMotionSequenceDuration(sequence: MotionTrackingSequence): number {
  const explicitDuration = Number.isFinite(sequence.duration) ? Math.max(0, sequence.duration) : 0;
  const frameDuration = Math.max(0, sequence.frameCount || sequence.frames.length || 1) / Math.max(1, sequence.fps);
  return Math.max(explicitDuration, frameDuration);
}

export function getMotionSourceDuration(userSource: CanvasImageSource | null): number | null {
  if (!(userSource instanceof HTMLVideoElement)) {
    return null;
  }
  const duration = userSource.duration;
  if (!Number.isFinite(duration) || duration <= 0) {
    return null;
  }
  return duration;
}

export function getEffectiveMotionPlaybackDuration(
  sequence: MotionTrackingSequence,
  userSource: CanvasImageSource | null,
  loopEnabled = false
): number {
  return getMotionPlaybackDuration(
    getMotionSequenceDuration(sequence),
    getMotionSourceDuration(userSource),
    loopEnabled
  );
}

export function clampMotionSequenceTime(sequence: MotionTrackingSequence, time: number): number {
  return Math.max(0, Math.min(time, getMotionSequenceDuration(sequence)));
}

export function getMotionSequencePlaybackTime(
  sequence: MotionTrackingSequence,
  time: number,
  loopEnabled = false
): number {
  return getMotionTemplatePlaybackTime(getMotionSequenceDuration(sequence), time, loopEnabled);
}

export async function seekMotionVideos(
  atlasVideo: HTMLVideoElement | null,
  userSource: CanvasImageSource | null,
  time: number,
  signal?: AbortSignal
): Promise<void> {
  await Promise.all([
    atlasVideo ? seekVideo(atlasVideo, time, { signal }) : Promise.resolve(),
    userSource instanceof HTMLVideoElement ? seekVideo(userSource, time, { signal }) : Promise.resolve()
  ]);
}

/**
 * Loads the neutral atlas the first time it is needed, once.
 *
 * It is never fetched up front: Custom colors starts switched off, and paying
 * for a second atlas on every template that merely has the capability would make
 * those templates slower to open for no benefit.
 */
export function getActiveAtlasVideo(
  assets: LoadedMotionAssets,
  adjustments: MotionColorMatteAdjustments | null | undefined
): HTMLVideoElement {
  // Only the object colours need the neutral atlas. The Background tint reads
  // luminance from whichever atlas is already in play and swaps nothing, so it
  // must not pull the neutral one in — doing so left the drawn atlas unseeked
  // and frozen while its counterpart advanced.
  //
  // The caller passes the adjustments rather than a precomputed flag on purpose:
  // renderFrame and the preview seek both go through here, so the atlas that is
  // drawn and the atlas that is seeked cannot drift apart again.
  const channelsActive =
    resolveActiveMatteChannels(assets.template.motionColorMatte?.channels, adjustments).length > 0;
  return channelsActive && assets.colorMatteVideo && assets.neutralAtlasVideo && assets.neutralAtlasReady
    ? assets.neutralAtlasVideo
    : assets.atlasVideo;
}

export async function ensureNeutralAtlas(
  assets: LoadedMotionAssets,
  resolveAtlasTime: () => number
): Promise<void> {
  if (!assets.neutralAtlasUrl) {
    return;
  }

  // Keep playback on the already-synchronised ordinary atlas while the neutral
  // one catches up. Re-enabling Custom colors later needs this too, because the
  // neutral video stopped advancing while it was not selected.
  assets.neutralAtlasReady = false;
  try {
    const video = await loadNeutralAtlasVideo(assets);
    const atlasTime = resolveAtlasTime();
    if (Number.isFinite(atlasTime)) {
      await seekVideo(video, atlasTime);
    }
    assets.neutralAtlasReady = true;
  } catch {
    assets.neutralAtlasReady = false;
  }
}

/** Export must not start until every user-enabled colour asset is decodable. */
export async function ensureMotionExportColorAssets(
  assets: LoadedMotionAssets,
  adjustments: MotionColorMatteAdjustments | null | undefined
): Promise<void> {
  const channelsActive =
    resolveActiveMatteChannels(assets.template.motionColorMatte?.channels, adjustments).length > 0;
  const backgroundActive = isBackgroundTintActive(Boolean(assets.backgroundMatteUrl), adjustments);
  const pending: Promise<HTMLVideoElement>[] = [];

  if (channelsActive) {
    if (!assets.colorMatteUrl) {
      throw new Error('Custom colors cannot be exported because the Puzzle Matte video is missing.');
    }
    pending.push(loadColorMatteVideo(assets));
    if (assets.neutralAtlasUrl) {
      pending.push(loadNeutralAtlasVideo(assets));
    }
  }

  if (backgroundActive) {
    if (!assets.backgroundMatteUrl) {
      throw new Error('Background colors cannot be exported because the background matte video is missing.');
    }
    pending.push(loadBackgroundMatteVideo(assets));
  }

  try {
    await Promise.all(pending);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'an auxiliary video could not be loaded';
    throw new Error(`Motion color assets are not ready: ${message}`);
  }
}

/** Preview counterpart to the strict export gate. Only videos used by the
 * current switches are loaded, keeping inactive decoders out of playback. */
export async function ensureMotionPreviewColorAssets(
  assets: LoadedMotionAssets,
  adjustments: MotionColorMatteAdjustments | null | undefined
): Promise<void> {
  const channelsActive =
    resolveActiveMatteChannels(assets.template.motionColorMatte?.channels, adjustments).length > 0;
  const backgroundActive = isBackgroundTintActive(Boolean(assets.backgroundMatteUrl), adjustments);
  const pending: Promise<HTMLVideoElement>[] = [];

  if (channelsActive && assets.colorMatteUrl) {
    pending.push(loadColorMatteVideo(assets));
  }
  if (backgroundActive && assets.backgroundMatteUrl) {
    pending.push(loadBackgroundMatteVideo(assets));
  }

  await Promise.all(pending);
}

function prepareAuxiliaryVideo(video: HTMLVideoElement): HTMLVideoElement {
  video.pause();
  video.muted = true;
  video.playsInline = true;
  return video;
}

function loadNeutralAtlasVideo(assets: LoadedMotionAssets): Promise<HTMLVideoElement> {
  if (assets.neutralAtlasVideo) return Promise.resolve(assets.neutralAtlasVideo);
  if (!assets.neutralAtlasUrl) return Promise.reject(new Error('Neutral motion atlas is unavailable.'));
  if (!assets.neutralAtlasLoading) {
    assets.neutralAtlasReady = false;
    const loading = loadVideoFromUrl(assets.neutralAtlasUrl)
      .then(prepareAuxiliaryVideo)
      .then((video) => {
        assets.neutralAtlasVideo = video;
        return video;
      })
      .finally(() => {
        if (assets.neutralAtlasLoading === loading) assets.neutralAtlasLoading = null;
      });
    assets.neutralAtlasLoading = loading;
  }
  return assets.neutralAtlasLoading;
}

function loadColorMatteVideo(assets: LoadedMotionAssets): Promise<HTMLVideoElement> {
  if (assets.colorMatteVideo) return Promise.resolve(assets.colorMatteVideo);
  if (!assets.colorMatteUrl) return Promise.reject(new Error('Puzzle Matte video is unavailable.'));
  if (!assets.colorMatteLoading) {
    const loading = loadVideoFromUrl(assets.colorMatteUrl)
      .then(prepareAuxiliaryVideo)
      .then((video) => {
        assets.colorMatteVideo = video;
        return video;
      })
      .finally(() => {
        if (assets.colorMatteLoading === loading) assets.colorMatteLoading = null;
      });
    assets.colorMatteLoading = loading;
  }
  return assets.colorMatteLoading;
}

function loadBackgroundMatteVideo(assets: LoadedMotionAssets): Promise<HTMLVideoElement> {
  if (assets.backgroundMatteVideo) return Promise.resolve(assets.backgroundMatteVideo);
  if (!assets.backgroundMatteUrl) return Promise.reject(new Error('Background matte video is unavailable.'));
  if (!assets.backgroundMatteLoading) {
    const loading = loadVideoFromUrl(assets.backgroundMatteUrl)
      .then(prepareAuxiliaryVideo)
      .then((video) => {
        assets.backgroundMatteVideo = video;
        return video;
      })
      .finally(() => {
        if (assets.backgroundMatteLoading === loading) assets.backgroundMatteLoading = null;
      });
    assets.backgroundMatteLoading = loading;
  }
  return assets.backgroundMatteLoading;
}

/**
 * Moves the matte to the same timestamp as the atlas. The two videos share a
 * frame count and frame rate by contract, so one time value drives both.
 *
 * A matte that fails to seek resolves anyway: falling a frame behind is a far
 * smaller problem than stalling the preview loop.
 */
export function seekMotionColorMatte(
  assets: LoadedMotionAssets,
  atlasTime: number,
  adjustments: MotionColorMatteAdjustments | null | undefined,
  signal?: AbortSignal
): Promise<void> {
  const colorMatteActive =
    resolveActiveMatteChannels(assets.template.motionColorMatte?.channels, adjustments).length > 0;
  const backgroundMatteActive = isBackgroundTintActive(Boolean(assets.backgroundMatteUrl), adjustments);
  return Promise.all([
    colorMatteActive && assets.colorMatteVideo
      ? seekVideo(assets.colorMatteVideo, atlasTime, { signal }).catch(() => undefined)
      : Promise.resolve(),
    backgroundMatteActive && assets.backgroundMatteVideo
      ? seekVideo(assets.backgroundMatteVideo, atlasTime, { signal }).catch(() => undefined)
      : Promise.resolve()
  ]).then(() => undefined);
}

/** Export fallback: unlike preview, a failed seek must stop instead of dropping a layer. */
export function seekMotionColorMatteStrict(
  assets: LoadedMotionAssets,
  atlasTime: number,
  options: {
    colorMatte: boolean;
    backgroundMatte: boolean;
    signal?: AbortSignal;
  }
): Promise<void> {
  return Promise.all([
    options.colorMatte && assets.colorMatteVideo
      ? seekVideo(assets.colorMatteVideo, atlasTime, { signal: options.signal })
      : Promise.resolve(),
    options.backgroundMatte && assets.backgroundMatteVideo
      ? seekVideo(assets.backgroundMatteVideo, atlasTime, { signal: options.signal })
      : Promise.resolve()
  ]).then(() => undefined);
}

export function renderFrame(
  motionAssets: LoadedMotionAssets,
  userSource: CanvasImageSource | null,
  frameIndex: number,
  options: MotionRenderOptions = {}
): HTMLCanvasElement {
  const frame = resolveTrackingFrame(motionAssets.tracking, frameIndex);
  const trackingQuad = options.trackingQuadOverride && options.trackingQuadOverride.length === 4
    ? options.trackingQuadOverride
    : frame.screenQuad;
  const frame2 = motionAssets.tracking2 ? resolveTrackingFrame(motionAssets.tracking2, frameIndex) : null;
  const trackingQuad2 = options.trackingQuadOverride2 && options.trackingQuadOverride2.length === 4
    ? options.trackingQuadOverride2
    : frame2?.screenQuad || null;
  const frame3 = motionAssets.tracking3 ? resolveTrackingFrame(motionAssets.tracking3, frameIndex) : null;
  const trackingQuad3 = options.trackingQuadOverride3 && options.trackingQuadOverride3.length === 4
    ? options.trackingQuadOverride3
    : frame3?.screenQuad || null;
  // With the object colours on, the whole atlas is swapped for the neutral one:
  // its screen mask, aux and overlay tiles are identical, only the recolourable
  // objects differ, so nothing else in the frame changes.
  const atlasSource = options.atlasSourceOverride || getActiveAtlasVideo(motionAssets, options.colorMatte);

  const atlasFrameAssets = extractAtlasFrameAssets(
    motionAssets.template,
    atlasSource,
    motionAssets.atlasSpec,
    motionAssets.template.atlasLayout || 'standard-4'
  );
  const gridDensity = options.realtime ? 14 : 42;
  const reflection = options.realtime
    ? reduceRealtimeReflection(options.reflection)
    : options.reflection;

  // Built as a layer rather than folded into the base, so the compositor can
  // paint it after the Color Control sliders have had their say.
  const colorMatteOverlay = buildMotionColorMatteOverlay(
    atlasFrameAssets.base,
    {
      matte: options.colorMatteSourceOverride || motionAssets.colorMatteVideo,
      backgroundMatte: options.backgroundMatteSourceOverride || motionAssets.backgroundMatteVideo
    },
    motionAssets.template.motionColorMatte?.channels,
    options.colorMatte
  );

  return renderMaster(atlasFrameAssets, userSource, {
    userImage2: options.userSource2,
    userImage3: options.userSource3,
    diffuseOverlay: colorMatteOverlay,
    renderSize: options.renderSize || motionAssets.template.outputSize || DEFAULT_OUTPUT_SIZE,
    screenRenderScale: options.screenRenderScale,
    gridX: options.gridX ?? gridDensity,
    gridY: options.gridY ?? gridDensity,
    diffuse: options.diffuse,
    background: options.background,
    reflection,
    screenOverlay: options.screenOverlay,
    useAlternateMask: options.useAlternateMask,
    customPlacement: options.customPlacement,
    customPlacement2: options.customPlacement2,
    customPlacement3: options.customPlacement3,
    warpMode: options.warpMode,
    warpMode2: options.warpMode2,
    warpMode3: options.warpMode3,
    realtime: options.realtime,
    forExport: !options.realtime,
    screenQuadOverride: scaleTrackingQuad(
      trackingQuad,
      motionAssets.tracking,
      motionAssets.template.outputSize || DEFAULT_OUTPUT_SIZE
    ),
    screenQuadOverride2: trackingQuad2 && motionAssets.tracking2
      ? scaleTrackingQuad(
          trackingQuad2,
          motionAssets.tracking2,
          motionAssets.template.outputSize || DEFAULT_OUTPUT_SIZE
        )
      : undefined,
    screenQuadOverride3: trackingQuad3 && motionAssets.tracking3
      ? scaleTrackingQuad(
          trackingQuad3,
          motionAssets.tracking3,
          motionAssets.template.outputSize || DEFAULT_OUTPUT_SIZE
        )
      : undefined
  });
}

export function interpolateTrackingQuadAtTime(sequence: MotionTrackingSequence, time: number): Point[] {
  const maxFrameIndex = Math.max(0, sequence.frameCount - 1);
  const rawFrame = Math.max(
    0,
    Math.min(time * Math.max(1, sequence.fps) + TRACKING_FRAME_EXPORT_OFFSET, maxFrameIndex)
  );
  const lowerIndex = Math.floor(rawFrame);
  const upperIndex = Math.ceil(rawFrame);

  const lowerFrame = resolveTrackingFrame(sequence, lowerIndex);
  if (upperIndex === lowerIndex) {
    return lowerFrame.screenQuad.map((point) => ({ ...point }));
  }

  const upperFrame = resolveTrackingFrame(sequence, upperIndex);
  const t = rawFrame - lowerIndex;

  return lowerFrame.screenQuad.map((point, index) => {
    const nextPoint = upperFrame.screenQuad[index] || point;
    return {
      x: point.x + (nextPoint.x - point.x) * t,
      y: point.y + (nextPoint.y - point.y) * t
    };
  });
}

export function getMotionResizeDimensions(
  resizeMode: MotionResizeMode,
  squareRenderSize: number
): MotionResizeDimensions {
  const safeSquareSize = Math.max(1, Math.round(squareRenderSize));
  if (resizeMode === '16:9') {
    const cropHeight = Math.max(1, Math.round(safeSquareSize / MOTION_WIDESCREEN_ASPECT));
    const cropY = Math.max(0, Math.round((safeSquareSize - cropHeight) / 2));
    return {
      outputWidth: safeSquareSize,
      outputHeight: cropHeight,
      sourceCropX: 0,
      sourceCropY: cropY,
      sourceCropWidth: safeSquareSize,
      sourceCropHeight: cropHeight
    };
  }

  if (resizeMode === '9:16') {
    const cropWidth = Math.max(1, Math.round(safeSquareSize * MOTION_VERTICAL_ASPECT));
    const cropX = Math.max(0, Math.round((safeSquareSize - cropWidth) / 2));
    return {
      outputWidth: cropWidth,
      outputHeight: safeSquareSize,
      sourceCropX: cropX,
      sourceCropY: 0,
      sourceCropWidth: cropWidth,
      sourceCropHeight: safeSquareSize
    };
  }

  return {
    outputWidth: safeSquareSize,
    outputHeight: safeSquareSize,
    sourceCropX: 0,
    sourceCropY: 0,
    sourceCropWidth: safeSquareSize,
    sourceCropHeight: safeSquareSize
  };
}

export function createMotionViewportCanvas(
  source: CanvasImageSource,
  resizeMode: MotionResizeMode,
  options: {
    squareRenderSize: number;
    targetWidth?: number;
    targetHeight?: number;
  }
): HTMLCanvasElement {
  const viewport = getMotionResizeDimensions(resizeMode, options.squareRenderSize);
  const targetWidth = Math.max(1, Math.round(options.targetWidth ?? viewport.outputWidth));
  const targetHeight = Math.max(1, Math.round(options.targetHeight ?? viewport.outputHeight));
  const canvas = createCanvas(targetWidth, targetHeight);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('2D context is not available for motion viewport canvas');
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    source,
    viewport.sourceCropX,
    viewport.sourceCropY,
    viewport.sourceCropWidth,
    viewport.sourceCropHeight,
    0,
    0,
    targetWidth,
    targetHeight
  );
  return canvas;
}

async function loadMotionTracking(
  url: string,
  template: MockupTemplate
): Promise<{
  primary: MotionTrackingSequence;
  secondary: MotionTrackingSequence | null;
  tertiary: MotionTrackingSequence | null;
}> {
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Motion tracking request failed: ${response.status}`);
  }

  const payload = (await response.json()) as unknown;
  if (!payload || typeof payload !== 'object') {
    throw new Error('Motion tracking JSON is invalid.');
  }

  const value = payload as Record<string, unknown>;
  if (isMultiScreenTrackingDocument(payload)) {
    const expectedTrackCount = template.screenProfile?.id === 'triple-screen'
      ? 3
      : template.screenProfile?.id === 'dual-screen'
        ? 2
        : 1;
    if (expectedTrackCount === 1 || payload.tracks.length !== expectedTrackCount) {
      throw new Error(`Tracking document does not match the template's ${expectedTrackCount}-screen profile.`);
    }
    const outputWidth = template.outputSize ?? DEFAULT_OUTPUT_SIZE;
    const outputHeight = template.outputSize ?? DEFAULT_OUTPUT_SIZE;
    const firstTrack = payload.tracks.find((track) => track.id === 'screen-1');
    const secondTrack = payload.tracks.find((track) => track.id === 'screen-2');
    const thirdTrack = payload.tracks.find((track) => track.id === 'screen-3');
    if (!firstTrack || !secondTrack || (expectedTrackCount === 3 && !thirdTrack)) {
      throw new Error('Multi-screen tracking JSON is missing a required screen track.');
    }
    const fromTrack = (track: typeof firstTrack): MotionTrackingSequence => {
      const frames = track.frames.map((frame, index) => ({
        index,
        time: frame.t,
        screenQuad: frame.points.map((point) => ({ x: point.x, y: point.y }))
      }));
      return createMotionTrackingSequence({
        fps: payload.fps,
        frameCount: payload.frameCount,
        duration: payload.duration,
        outputWidth,
        outputHeight,
        frames
      });
    };
    return {
      primary: fromTrack(firstTrack),
      secondary: fromTrack(secondTrack),
      tertiary: thirdTrack ? fromTrack(thirdTrack) : null
    };
  }
  const outputWidth = positiveNumber(value.outputWidth) ?? template.outputSize ?? DEFAULT_OUTPUT_SIZE;
  const outputHeight = positiveNumber(value.outputHeight) ?? template.outputSize ?? DEFAULT_OUTPUT_SIZE;
  const fps = positiveNumber(value.fps) ?? template.fps ?? DEFAULT_FPS;
  const rawFrames = Array.isArray(value.frames) ? value.frames : [];
  const frames = rawFrames
    .map((entry, fallbackIndex) => normalizeTrackingFrame(entry, fallbackIndex, outputWidth, outputHeight, fps))
    .filter((frame): frame is MotionTrackingFrame => frame !== null);

  if (frames.length === 0) {
    throw new Error('Motion tracking JSON does not contain valid frames.');
  }

  return {
    primary: createMotionTrackingSequence({
      fps,
      frameCount: positiveInteger(value.frameCount) ?? frames.length,
      duration: positiveNumber(value.duration) ?? frames.length / fps,
      outputWidth,
      outputHeight,
      frames
    }),
    secondary: null,
    tertiary: null
  };
}

function createMotionTrackingSequence(
  value: Pick<MotionTrackingSequence, 'fps' | 'frameCount' | 'duration' | 'outputWidth' | 'outputHeight' | 'frames'>
): MotionTrackingSequence {
  const frameMap = new Map<number, MotionTrackingFrame>();
  for (const frame of value.frames) {
    frameMap.set(frame.index, frame);
  }
  return {
    ...value,
    frameMap,
    bounds: computeTrackingBounds(value.frames),
    staticQuad: value.frames.every((frame) => isSameQuad(frame.screenQuad, value.frames[0].screenQuad))
  };
}

function normalizeTrackingFrame(
  entry: unknown,
  fallbackIndex: number,
  outputWidth: number,
  outputHeight: number,
  fps: number
): MotionTrackingFrame | null {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const value = entry as Record<string, unknown>;
  const screenQuad =
    parseQuad(value.screenQuad) ??
    scaleNormalizedQuad(parseQuad(value.screenQuadNormalized), outputWidth, outputHeight);

  if (!screenQuad) {
    return null;
  }

  return {
    index: positiveInteger(value.index) ?? fallbackIndex,
    time: finiteNumber(value.time) ?? fallbackIndex / fps,
    screenQuad
  };
}

function parseQuad(value: unknown): Point[] | null {
  if (!Array.isArray(value) || value.length !== 4) {
    return null;
  }

  const points = value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }
      const point = entry as Record<string, unknown>;
      const x = Number(point.x);
      const y = Number(point.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        return null;
      }
      return { x, y };
    })
    .filter((point): point is Point => point !== null);

  return points.length === 4 ? points : null;
}

function scaleNormalizedQuad(points: Point[] | null, outputWidth: number, outputHeight: number): Point[] | null {
  if (!points) {
    return null;
  }

  return points.map((point) => ({
    x: point.x * outputWidth,
    y: point.y * outputHeight
  }));
}

function resolveTrackingFrame(sequence: MotionTrackingSequence, frameIndex: number): MotionTrackingFrame {
  const clampedIndex = Math.max(0, Math.min(frameIndex + TRACKING_FRAME_OFFSET, sequence.frameCount - 1));
  const exact = sequence.frameMap.get(clampedIndex);
  if (exact) {
    return exact;
  }

  const byPosition = sequence.frames[Math.min(clampedIndex, sequence.frames.length - 1)];
  if (byPosition) {
    return byPosition;
  }

  return sequence.frames[0];
}

function scaleTrackingQuad(frameQuad: Point[], tracking: MotionTrackingSequence, targetSize: number): Point[] {
  if (tracking.outputWidth === targetSize && tracking.outputHeight === targetSize) {
    return frameQuad;
  }

  const scaleX = targetSize / Math.max(1, tracking.outputWidth);
  const scaleY = targetSize / Math.max(1, tracking.outputHeight);

  return frameQuad.map((point) => ({
    x: point.x * scaleX,
    y: point.y * scaleY
  }));
}

// Module-level cache keyed by (VideoFrame.timestamp, atlasSpec object reference, blur flag).
// This avoids re-extracting identical tile canvases when the atlas frame hasn't changed
// between consecutive render calls (e.g. 30 fps export with a 24 fps atlas video).
let _atlasTileCache: {
  timestamp: number;
  spec: MotionAtlasSpec;
  layout: MotionAtlasLayout;
  templateId: string;
  screenBlurEnabled: boolean;
  alternateScreenMaskEnabled: boolean;
  atlasContract: MotionAtlasContract;
  assets: LoadedMockupAssets;
} | null = null;

function extractAtlasFrameAssets(
  template: MockupTemplate,
  atlasVideo: CanvasImageSource,
  atlasSpec: MotionAtlasSpec,
  layout: MotionAtlasLayout
): LoadedMockupAssets {
  const atlasAuxRole = resolveMotionAtlasAuxRole({
    atlasLayout: layout,
    screenBlurEnabled: template.screenBlurEnabled,
    motionDynamicIslandToggle: template.motionDynamicIslandToggle
  });
  const screenCount = getTemplateInputCount(template);
  const duo = normalizeFlexibleDisplay(template.flexibleDisplay);
  const multiScreenEnabled = screenCount > 1;
  const alternateScreenMaskEnabled = atlasAuxRole === 'alternate-screen-mask';
  const screenBlurEnabled = atlasAuxRole === 'screen-blur-mask';
  const atlasContract = resolveMotionAtlasContract(template);
  // VideoFrame objects carry a precise timestamp we can use as a cache key.
  const cacheTimestamp =
    typeof VideoFrame !== 'undefined' && atlasVideo instanceof VideoFrame
      ? atlasVideo.timestamp
      : -1;

  if (
    _atlasTileCache !== null &&
    cacheTimestamp >= 0 &&
    _atlasTileCache.timestamp === cacheTimestamp &&
    _atlasTileCache.templateId === template.id &&
    _atlasTileCache.spec === atlasSpec &&
    _atlasTileCache.screenBlurEnabled === screenBlurEnabled &&
    _atlasTileCache.alternateScreenMaskEnabled === alternateScreenMaskEnabled &&
    _atlasTileCache.atlasContract === atlasContract
  ) {
    return _atlasTileCache.assets;
  }

  if (
    _atlasTileCache !== null &&
    getTemplateInputCount(_atlasTileCache.assets.template) === screenCount &&
    hasSameAtlasCanvasShape(
      _atlasTileCache,
      atlasSpec,
      layout,
      screenBlurEnabled,
      alternateScreenMaskEnabled,
      atlasContract
    )
  ) {
    const assets = _atlasTileCache.assets;
    assets.template = template;
    redrawAtlasTile(assets.base, atlasVideo, atlasSpec, 0, 0);
    redrawAtlasTile(assets.mask, atlasVideo, atlasSpec, 1, 0);
    if (assets.mask2 && !multiScreenEnabled) redrawAtlasTile(assets.mask2, atlasVideo, atlasSpec, 0, 1);
    if (assets.screenBlurMask) redrawAtlasTile(assets.screenBlurMask, atlasVideo, atlasSpec, 0, 1);
    if (
      atlasContract === 'screen-overlay-v1' ||
      atlasContract === 'dual-screen-packed-v1' ||
      atlasContract === 'triple-screen-packed-v1' || atlasContract === 'duo-packed-v1'
    ) redrawAtlasTile(assets.overlay, atlasVideo, atlasSpec, 1, 1);
    if (assets.backgroundMask) redrawAtlasTile(assets.backgroundMask, atlasVideo, atlasSpec, 2, 0);
    if (assets.reflectionMask) redrawAtlasTile(assets.reflectionMask, atlasVideo, atlasSpec, 1, 1);
    if (assets.reflectionBlurMask) redrawAtlasTile(assets.reflectionBlurMask, atlasVideo, atlasSpec, 2, 1);

    _atlasTileCache.timestamp = cacheTimestamp;
    _atlasTileCache.spec = atlasSpec;
    _atlasTileCache.templateId = template.id;
    return assets;
  }

  const base = extractAtlasTile(atlasVideo, atlasSpec, 0, 0);
  const mask = extractAtlasTile(atlasVideo, atlasSpec, 1, 0);
  // standard-4: (0,1) is either blur, an explicit alternate screen mask, or ignored.
  // extended-6: (0,1) remains the legacy alternate screen mask.
  const mask2 = multiScreenEnabled
    ? mask
    : alternateScreenMaskEnabled
      ? extractAtlasTile(atlasVideo, atlasSpec, 0, 1)
      : null;
  const mask3 = screenCount === 3 ? mask : null;
  const screenBlurMask = screenBlurEnabled
    ? extractAtlasTile(atlasVideo, atlasSpec, 0, 1)
    : null;
  const overlay =
    atlasContract === 'screen-overlay-v1' ||
    atlasContract === 'dual-screen-packed-v1' ||
    atlasContract === 'triple-screen-packed-v1' || atlasContract === 'duo-packed-v1'
      ? extractAtlasTile(atlasVideo, atlasSpec, 1, 1)
      : createTransparentTile(atlasSpec.tileWidth, atlasSpec.tileHeight);
  const backgroundMask = layout === 'extended-6' ? extractAtlasTile(atlasVideo, atlasSpec, 2, 0) : null;
  const reflectionMask =
    atlasContract === 'legacy-reflection-v1' ? extractAtlasTile(atlasVideo, atlasSpec, 1, 1) : null;
  const reflectionBlurMask =
    atlasContract === 'legacy-reflection-v1' && layout === 'extended-6'
      ? extractAtlasTile(atlasVideo, atlasSpec, 2, 1)
      : null;

  const assets: LoadedMockupAssets = {
    template,
    base,
    mask,
    maskChannel: multiScreenEnabled || (duo && duo.mode !== 'outer') ? 'red' : 'luminance',
    mask2,
    mask2Channel: multiScreenEnabled ? 'green' : 'luminance',
    mask3,
    mask3Channel: screenCount === 3 ? 'blue' : 'luminance',
    overlay,
    backgroundMask,
    reflectionMask,
    reflectionBlurMask,
    screenBlurMask
  };

  _atlasTileCache = {
    timestamp: cacheTimestamp,
    spec: atlasSpec,
    layout,
    templateId: template.id,
    screenBlurEnabled,
    alternateScreenMaskEnabled,
    atlasContract,
    assets
  };

  return assets;
}

function extractAtlasTile(
  atlasVideo: CanvasImageSource,
  atlasSpec: MotionAtlasSpec,
  column: number,
  row: number
): HTMLCanvasElement {
  const canvas = createCanvas(atlasSpec.tileWidth, atlasSpec.tileHeight);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context unavailable for motion atlas tile.');
  }

  ctx.drawImage(
    atlasVideo,
    column * atlasSpec.tileWidth,
    row * atlasSpec.tileHeight,
    atlasSpec.tileWidth,
    atlasSpec.tileHeight,
    0,
    0,
    atlasSpec.tileWidth,
    atlasSpec.tileHeight
  );

  return canvas;
}

function hasSameAtlasCanvasShape(
  cache: NonNullable<typeof _atlasTileCache>,
  spec: MotionAtlasSpec,
  layout: MotionAtlasLayout,
  screenBlurEnabled: boolean,
  alternateScreenMaskEnabled: boolean,
  atlasContract: MotionAtlasContract
): boolean {
  return (
    cache.spec.tileWidth === spec.tileWidth &&
    cache.spec.tileHeight === spec.tileHeight &&
    cache.layout === layout &&
    cache.screenBlurEnabled === screenBlurEnabled &&
    cache.alternateScreenMaskEnabled === alternateScreenMaskEnabled &&
    cache.atlasContract === atlasContract
  );
}

function redrawAtlasTile(
  target: CanvasImageSource,
  atlasVideo: CanvasImageSource,
  atlasSpec: MotionAtlasSpec,
  column: number,
  row: number
): void {
  if (!(target instanceof HTMLCanvasElement)) {
    throw new Error('Cached motion atlas tile is not a canvas.');
  }
  const ctx = target.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context unavailable for cached motion atlas tile.');
  }
  ctx.clearRect(0, 0, target.width, target.height);
  ctx.drawImage(
    atlasVideo,
    column * atlasSpec.tileWidth,
    row * atlasSpec.tileHeight,
    atlasSpec.tileWidth,
    atlasSpec.tileHeight,
    0,
    0,
    atlasSpec.tileWidth,
    atlasSpec.tileHeight
  );
}

function createTransparentTile(width: number, height: number): HTMLCanvasElement {
  return createCanvas(width, height);
}

function computeTrackingBounds(frames: MotionTrackingFrame[]): MotionTrackingSequence['bounds'] {
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (const frame of frames) {
    for (const point of frame.screenQuad) {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    }
  }

  return {
    minX: Number.isFinite(minX) ? minX : 0,
    maxX: Number.isFinite(maxX) ? maxX : 0,
    minY: Number.isFinite(minY) ? minY : 0,
    maxY: Number.isFinite(maxY) ? maxY : 0
  };
}

function isSameQuad(a: Point[], b: Point[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((point, index) => {
    const other = b[index];
    return Math.abs(point.x - other.x) < 1e-6 && Math.abs(point.y - other.y) < 1e-6;
  });
}

function reduceRealtimeReflection(reflection?: ReflectionAdjustments): ReflectionAdjustments | undefined {
  if (!reflection) {
    return undefined;
  }

  return {
    ...reflection,
    // Playback preview should stay responsive on weaker devices.
    // Keep reflection visible, but remove blur work entirely in realtime mode.
    blur: 0
  };
}

function positiveNumber(value: unknown): number | null {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
}

function positiveInteger(value: unknown): number | null {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 0) {
    return null;
  }
  return parsed;
}

function finiteNumber(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
